/**
 * Primitivas visuais compartilhadas para timelines de andamentos.
 *
 * Mantém apenas apresentação; autoria, permissões e ações são resolvidas pelo
 * módulo consumidor para não misturar regras de negócio entre domínios.
 *
 * RELEASE: 2026-10-09 v1.0.0 - Extração do padrão de Tarefas/Retenção para reuso na OS.
 */
import type { ReactNode } from 'react';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';

interface TimelineDateSeparatorProps {
  label: string;
}

/** Separa grupos de uma timeline por dia no padrão visual de Tarefas/Retenção. */
export function TimelineDateSeparator({ label }: TimelineDateSeparatorProps) {
  return (
    <div className="my-4 flex items-center justify-center">
      <div className="h-px flex-1 bg-border" />
      <span className="rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

interface TimelineActivityCardProps {
  avatarSrc?: string | null;
  avatarName?: string | null;
  author: ReactNode;
  timestamp: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Cartão-base de andamento manual ou automático.
 * Slots preservam as particularidades de cada módulo sem duplicar o leiaute.
 */
export function TimelineActivityCard({
  avatarSrc,
  avatarName,
  author,
  timestamp,
  actions,
  children,
  footer,
  className,
}: TimelineActivityCardProps) {
  return (
    <article className="group flex gap-3">
      <div className="shrink-0 pt-1">
        <SmartAvatar
          src={avatarSrc || undefined}
          name={avatarName || 'Sistema'}
          size="sm"
          className="border-2 border-background shadow-sm"
        />
      </div>
      <div className={cn('min-w-0 flex-1 rounded-lg border bg-card shadow-sm transition-all hover:shadow-md', className)}>
        <header className="flex items-center justify-between gap-2 rounded-t-lg px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">{author}</div>
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            {timestamp}
          </div>
        </header>
        <div className="px-3 py-2">{children}</div>
        {footer}
      </div>
    </article>
  );
}